import React from "react";
import "./MatrixRain.css";

/**
 * A dim, transparent "Matrix" rain of binary/digits drifting down the page,
 * drawn on a fixed full-screen canvas behind all content. Deliberately faint so
 * it reads as texture, not distraction. Honors prefers-reduced-motion (renders
 * nothing) and pauses when the tab is hidden.
 */
const MatrixRain: React.FC = () => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);

  React.useEffect(() => {
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Half-width katakana with a few digits mixed in, as in the films' rain.
    const GLYPHS =
      "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789";
    const FONT_SIZE = 16;

    // Read the canvas colors from the canvas itself, not from the root. Custom
    // properties inherit, so this picks up whichever theme owns this subtree —
    // which matters during a theme cross-fade, when the root has already
    // flipped to the incoming theme while this layer is still fading out.
    // Reading the root here painted the outgoing rain in the new theme's
    // palette: green rain turned grey the instant you switched away.
    const styles = getComputedStyle(canvas);
    const token = (name: string, fallback: string) =>
      styles.getPropertyValue(name).trim() || fallback;
    const BG = token("--canvas-bg-rgb", "10, 14, 10");
    const GLYPH = token("--canvas-glyph-rgb", "0, 255, 65");

    let columns = 0;
    let drops: number[] = [];
    let raf = 0;

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = `rgb(${BG})`;
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
      columns = Math.ceil(window.innerWidth / FONT_SIZE);
      drops = Array.from({ length: columns }, () =>
        Math.floor((Math.random() * window.innerHeight) / FONT_SIZE)
      );
    };

    const draw = () => {
      // Fade the previous frame toward the background to leave a trailing tail.
      ctx.fillStyle = `rgba(${BG}, 0.10)`;
      ctx.fillRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.font = `${FONT_SIZE}px "JetBrains Mono", monospace`;

      for (let i = 0; i < drops.length; i++) {
        const char = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        const x = i * FONT_SIZE;
        const y = drops[i] * FONT_SIZE;
        // Deliberately dim so the rain reads as texture, not as content.
        ctx.fillStyle = `rgba(${GLYPH}, 0.20)`;
        ctx.fillText(char, x, y);

        if (y > window.innerHeight && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
      raf = window.setTimeout(() => requestAnimationFrame(draw), 70);
    };

    setup();
    draw();

    const onResize = () => setup();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="matrix-rain" aria-hidden="true" />;
};

export default MatrixRain;
